import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sparkles,
  Compass,
  Target,
  FileText,
  GraduationCap,
  Award,
  Download,
  Share2,
  Flame,
  TrendingUp,
  FileDown,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  generateParentRecommendations,
  pillarLabel,
  pillarEmoji,
  type StudentSignals,
  type ParentRecommendation,
} from "@/lib/parentGuidance";
import jsPDF from "jspdf";

type Child = { id: string; full_name: string | null; email: string | null };

type GuideEntry = {
  id: string;
  parent_user_id: string;
  student_user_id: string;
  organization_id: string | null;
  month_key: string;
  recommendation_id: string;
  pillar: ParentRecommendation["pillar"];
  title: string;
  why: string;
  notes: string | null;
  completed_at: string | null;
};

const monthKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const prevMonthKey = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonthLabel = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
};

export default function Parent() {
  const { user } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [activeChild, setActiveChild] = useState<Child | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [signals, setSignals] = useState<StudentSignals>({});
  const [entries, setEntries] = useState<GuideEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const currentMonth = monthKey();

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: links } = await (supabase as any)
        .from("parent_child_links")
        .select("student_user_id, verified_at, organization_id")
        .eq("parent_user_id", user.id);
      const verified = (links ?? []).filter((l: any) => l.verified_at);
      const ids = verified.map((l: any) => l.student_user_id);
      if (ids.length === 0) {
        setLoading(false);
        return;
      }
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", ids);
      setChildren((profs ?? []) as Child[]);
      const first = (profs?.[0] as Child) ?? null;
      setActiveChild(first);
      const firstLink = verified.find((l: any) => l.student_user_id === first?.id);
      setOrgId(firstLink?.organization_id ?? null);
      setLoading(false);
    })();
  }, [user]);

  useEffect(() => {
    if (!activeChild) return;
    (async () => {
      const [{ data: assess }, { data: goals }, { data: vb }, { data: refl }, { data: unis }, { data: schols }] =
        await Promise.all([
          supabase.from("assessments").select("id, status, results").eq("user_id", activeChild.id).maybeSingle(),
          supabase.from("goals").select("id, status").eq("user_id", activeChild.id),
          supabase.from("vision_boards").select("id").eq("user_id", activeChild.id),
          supabase.from("monthly_reflections").select("month_index").eq("user_id", activeChild.id).order("month_index", { ascending: false }).limit(1).maybeSingle(),
          supabase.from("college_recommendations").select("id").eq("user_id", activeChild.id),
          supabase.from("scholarship_recommendations").select("id").eq("user_id", activeChild.id),
        ]);
      const results: any = (assess as any)?.results ?? {};
      const topClusters: string[] = results.clusters?.slice?.(0, 3).map((c: any) => c.name ?? c) ?? [];
      const topCareers: string[] = results.careers?.slice?.(0, 3).map((c: any) => c.title ?? c.name ?? c) ?? [];
      setSignals({
        hasCareerDna: !!assess,
        topClusters,
        topCareers,
        visionBoardCount: vb?.length ?? 0,
        goalsCount: goals?.length ?? 0,
        goalsCompleted: goals?.filter((g: any) => g.status === "completed").length ?? 0,
        lastReflectionMonth: (refl as any)?.month_index ?? null,
        currentMonthIndex: new Date().getMonth(),
        futureSkillsGaps: results.futureSkillsGaps ?? [],
        universities: unis?.length ?? 0,
        scholarships: schols?.length ?? 0,
      });
    })();
  }, [activeChild]);

  useEffect(() => {
    if (!user || !activeChild) return;
    (async () => {
      const recs = generateParentRecommendations(signals);
      if (recs.length > 0) {
        const rows = recs.map((r) => ({
          parent_user_id: user.id,
          student_user_id: activeChild.id,
          organization_id: orgId,
          month_key: currentMonth,
          recommendation_id: r.id,
          pillar: r.pillar,
          title: r.title,
          why: r.why,
        }));
        await (supabase as any)
          .from("parent_guide_entries")
          .upsert(rows, {
            onConflict: "parent_user_id,student_user_id,month_key,recommendation_id",
            ignoreDuplicates: true,
          });
      }
      const { data } = await (supabase as any)
        .from("parent_guide_entries")
        .select("*")
        .eq("parent_user_id", user.id)
        .eq("student_user_id", activeChild.id)
        .order("month_key", { ascending: false })
        .order("created_at", { ascending: true });
      setEntries((data ?? []) as GuideEntry[]);
      const draft: Record<string, string> = {};
      for (const e of (data ?? []) as GuideEntry[]) {
        if (e.notes) draft[e.id] = e.notes;
      }
      setNotesDraft(draft);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeChild, orgId, JSON.stringify(signals)]);

  const currentEntries = useMemo(
    () => entries.filter((e) => e.month_key === currentMonth),
    [entries, currentMonth],
  );

  const monthGroups = useMemo(() => {
    const map = new Map<string, GuideEntry[]>();
    for (const e of entries) {
      if (!map.has(e.month_key)) map.set(e.month_key, []);
      map.get(e.month_key)!.push(e);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries]);

  const historyMonths = useMemo(
    () => monthGroups.filter(([k]) => k !== currentMonth),
    [monthGroups, currentMonth],
  );

  // Progress metrics across months
  const progressStats = useMemo(() => {
    let totalDone = 0;
    let total = 0;
    const perMonth: { key: string; label: string; done: number; total: number; pct: number }[] = [];
    for (const [key, list] of monthGroups) {
      const done = list.filter((e) => e.completed_at).length;
      total += list.length;
      totalDone += done;
      perMonth.push({
        key,
        label: formatMonthLabel(key),
        done,
        total: list.length,
        pct: list.length ? Math.round((done / list.length) * 100) : 0,
      });
    }
    // Streak: consecutive months (ending at current) with >=1 completion
    const perMonthAsc = [...perMonth].reverse();
    let streak = 0;
    let cursor = currentMonth;
    // ensure current-month bucket exists in map for streak calc
    const byKey = new Map(perMonthAsc.map((m) => [m.key, m]));
    for (let i = 0; i < 60; i++) {
      const bucket = byKey.get(cursor);
      if (bucket && bucket.done > 0) {
        streak += 1;
        cursor = prevMonthKey(cursor);
      } else {
        break;
      }
    }
    const overallPct = total ? Math.round((totalDone / total) * 100) : 0;
    return { overallPct, totalDone, total, streak, perMonth };
  }, [monthGroups, currentMonth]);

  const toggleDone = async (entry: GuideEntry) => {
    const next = entry.completed_at ? null : new Date().toISOString();
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, completed_at: next } : e)),
    );
    const { error } = await (supabase as any)
      .from("parent_guide_entries")
      .update({ completed_at: next })
      .eq("id", entry.id);
    if (error) {
      toast({ title: "Couldn't save", description: error.message, variant: "destructive" });
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, completed_at: entry.completed_at } : e)),
      );
    }
  };

  const saveNotes = async (entry: GuideEntry) => {
    const value = (notesDraft[entry.id] ?? "").trim() || null;
    const { error } = await (supabase as any)
      .from("parent_guide_entries")
      .update({ notes: value })
      .eq("id", entry.id);
    if (error) {
      toast({ title: "Couldn't save notes", description: error.message, variant: "destructive" });
      return;
    }
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, notes: value } : e)));
    toast({ title: "Notes saved" });
  };

  const buildGuideText = (list: GuideEntry[], month: string) => {
    const childName = activeChild?.full_name ?? "your child";
    const lines = [
      `🌱 Parent Growth Guide™ — ${formatMonthLabel(month)}`,
      `For: ${childName}`,
      `Rooting YOU in YOUR Purpose. — WeKIT™ Career Clarity 360`,
      "",
    ];
    for (const e of list) {
      lines.push(`${pillarEmoji(e.pillar)} ${pillarLabel(e.pillar)}`);
      lines.push(`  • ${e.title}`);
      lines.push(`    Why: ${e.why}`);
      if (e.notes) lines.push(`    Notes: ${e.notes}`);
      if (e.completed_at) lines.push(`    ✔ Completed ${new Date(e.completed_at).toLocaleDateString()}`);
      lines.push("");
    }
    return lines.join("\n");
  };

  const downloadGuide = () => {
    const text = buildGuideText(currentEntries, currentMonth);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `parent-growth-guide-${currentMonth}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("Parent Growth Guide", margin, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(`${formatMonthLabel(currentMonth)}`, margin, y);
    y += 16;
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`For: ${activeChild?.full_name ?? "your child"}`, margin, y);
    y += 12;
    doc.text("Rooting YOU in YOUR Purpose. — WeKIT Career Clarity 360", margin, y);
    y += 24;
    doc.setTextColor(0);

    const ensureSpace = (needed: number) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    for (const e of currentEntries) {
      ensureSpace(90);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(80);
      doc.text(`${pillarEmoji(e.pillar)}  ${pillarLabel(e.pillar).toUpperCase()}`, margin, y);
      y += 14;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(20);
      const titleLines = doc.splitTextToSize(e.title, pageWidth - margin * 2);
      doc.text(titleLines, margin, y);
      y += titleLines.length * 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(90);
      const whyLines = doc.splitTextToSize(`Why: ${e.why}`, pageWidth - margin * 2);
      doc.text(whyLines, margin, y);
      y += whyLines.length * 13 + 4;

      if (e.notes) {
        doc.setTextColor(50);
        const noteLines = doc.splitTextToSize(`Notes: ${e.notes}`, pageWidth - margin * 2);
        ensureSpace(noteLines.length * 13);
        doc.text(noteLines, margin, y);
        y += noteLines.length * 13 + 4;
      }

      if (e.completed_at) {
        doc.setTextColor(30, 130, 90);
        doc.text(
          `✔ Completed ${new Date(e.completed_at).toLocaleDateString()}`,
          margin,
          y,
        );
        y += 14;
      }

      y += 12;
      doc.setDrawColor(230);
      doc.line(margin, y - 8, pageWidth - margin, y - 8);
    }

    doc.save(`parent-growth-guide-${currentMonth}.pdf`);
  };

  const shareGuide = async () => {
    const text = buildGuideText(currentEntries, currentMonth);
    const title = `Parent Growth Guide™ — ${formatMonthLabel(currentMonth)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text });
        return;
      } catch {
        /* user cancelled */
      }
    }
    await navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard", description: "Paste it anywhere for home use." });
  };

  if (loading) return <div className="container py-10 text-muted-foreground">Loading…</div>;
  if (children.length === 0)
    return (
      <div className="container max-w-2xl py-16 text-center">
        <h1 className="text-3xl font-bold mb-3">No child linked yet</h1>
        <p className="text-muted-foreground mb-6">
          When your child's school sends you an invitation, you'll be linked here automatically.
        </p>
        <Link to="/"><Button variant="outline">Back home</Button></Link>
      </div>
    );

  return (
    <>
      <PageHead
        title="Parent Dashboard | WeKIT™ Career Clarity 360"
        description="Walk alongside your child's growth journey."
        path="/parent"
      />
      <div className="container max-w-5xl py-10 space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Parent Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Rooting {activeChild?.full_name ?? "your child"} in their purpose.
            </p>
          </div>
          {children.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {children.map((c) => (
                <Button
                  key={c.id}
                  size="sm"
                  variant={c.id === activeChild?.id ? "default" : "outline"}
                  onClick={() => setActiveChild(c)}
                >
                  {c.full_name ?? c.email}
                </Button>
              ))}
            </div>
          )}
        </header>

        <section className="grid md:grid-cols-3 gap-4">
          <ReadCard icon={<Sparkles className="w-4 h-4" />} title="Career DNA™" status={signals.hasCareerDna ? "Available" : "Not yet"} />
          <ReadCard icon={<Compass className="w-4 h-4" />} title="Growth Journey" status={signals.lastReflectionMonth != null ? `Last reflection · month ${signals.lastReflectionMonth + 1}` : "Not started"} />
          <ReadCard icon={<Target className="w-4 h-4" />} title="Goals" status={`${signals.goalsCompleted ?? 0}/${signals.goalsCount ?? 0} completed`} />
        </section>

        {/* Progress card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Your Guide Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Overall completion</div>
                <div className="text-3xl font-bold">{progressStats.overallPct}%</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {progressStats.totalDone}/{progressStats.total} recommendations
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Flame className="w-3.5 h-3.5" /> Monthly streak
                </div>
                <div className="text-3xl font-bold">{progressStats.streak}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {progressStats.streak === 1 ? "month" : "months"} in a row
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <div className="text-xs text-muted-foreground">This month</div>
                <div className="text-3xl font-bold">
                  {currentEntries.filter((e) => e.completed_at).length}/{currentEntries.length}
                </div>
                <div className="text-xs text-muted-foreground mt-1">completed so far</div>
              </div>
            </div>
            {progressStats.perMonth.length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Completion by month</div>
                {progressStats.perMonth.slice(0, 6).map((m) => (
                  <div key={m.key} className="grid grid-cols-[110px_1fr_60px] items-center gap-3">
                    <span className="text-xs text-muted-foreground truncate">{m.label}</span>
                    <Progress value={m.pct} className="h-2" />
                    <span className="text-xs text-right tabular-nums">{m.done}/{m.total}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/30">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                🌱 Parent Growth Guide™
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {formatMonthLabel(currentMonth)} — celebrate, explore, connect, support and grow together.
              </p>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <Button size="sm" variant="outline" onClick={shareGuide} disabled={currentEntries.length === 0}>
                <Share2 className="w-4 h-4 mr-1.5" /> Share
              </Button>
              <Button size="sm" variant="outline" onClick={downloadGuide} disabled={currentEntries.length === 0}>
                <Download className="w-4 h-4 mr-1.5" /> TXT
              </Button>
              <Button size="sm" onClick={exportPdf} disabled={currentEntries.length === 0}>
                <FileDown className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Your guide is being prepared. Check back in a moment.
              </p>
            ) : (
              currentEntries.map((e) => (
                <div
                  key={e.id}
                  className={`block border rounded-2xl p-4 transition ${
                    e.completed_at ? "bg-muted/40" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`rec-${e.id}`}
                      checked={!!e.completed_at}
                      onCheckedChange={() => toggleDone(e)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <Badge variant="secondary" className="mb-2">
                        {pillarEmoji(e.pillar)} {pillarLabel(e.pillar)}
                      </Badge>
                      <label
                        htmlFor={`rec-${e.id}`}
                        className={`font-medium block cursor-pointer ${
                          e.completed_at ? "line-through text-muted-foreground" : ""
                        }`}
                      >
                        {e.title}
                      </label>
                      <div className="text-sm text-muted-foreground mt-1">{e.why}</div>
                      {e.completed_at && (
                        <div className="text-xs text-primary mt-2">
                          ✔ Completed {new Date(e.completed_at).toLocaleDateString()}
                        </div>
                      )}

                      <div className="mt-3">
                        <div className="text-xs text-muted-foreground mb-1.5">
                          Your notes (what you tried, what happened)
                        </div>
                        <Textarea
                          value={notesDraft[e.id] ?? ""}
                          onChange={(ev) =>
                            setNotesDraft((prev) => ({ ...prev, [e.id]: ev.target.value }))
                          }
                          onBlur={() => {
                            if ((notesDraft[e.id] ?? "") !== (e.notes ?? "")) saveNotes(e);
                          }}
                          placeholder="Optional — jot down what happened this month."
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {historyMonths.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Previous Growth Guides</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Look back at what you focused on — and what you completed together.
              </p>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible>
                {historyMonths.map(([key, list]) => {
                  const done = list.filter((e) => e.completed_at).length;
                  return (
                    <AccordionItem key={key} value={key}>
                      <AccordionTrigger>
                        <div className="flex items-center justify-between w-full pr-3">
                          <span className="font-medium">{formatMonthLabel(key)}</span>
                          <Badge variant="outline">{done}/{list.length} completed</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3">
                          {list.map((e) => (
                            <div key={e.id} className="border rounded-xl p-3">
                              <Badge variant="secondary" className="mb-1.5">
                                {pillarEmoji(e.pillar)} {pillarLabel(e.pillar)}
                              </Badge>
                              <div className={`text-sm font-medium ${e.completed_at ? "line-through text-muted-foreground" : ""}`}>
                                {e.title}
                              </div>
                              {e.notes && (
                                <div className="text-xs text-muted-foreground mt-1.5 italic">
                                  "{e.notes}"
                                </div>
                              )}
                              {e.completed_at && (
                                <div className="text-xs text-primary mt-1">
                                  ✔ {new Date(e.completed_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
        )}

        <section className="grid md:grid-cols-3 gap-4">
          <ReadCard icon={<FileText className="w-4 h-4" />} title="Career Clarity Report" status="Read-only" />
          <ReadCard icon={<GraduationCap className="w-4 h-4" />} title="Universities" status={`${signals.universities ?? 0} suggested`} />
          <ReadCard icon={<Award className="w-4 h-4" />} title="Scholarships" status={`${signals.scholarships ?? 0} suggested`} />
        </section>

        <p className="text-xs text-muted-foreground text-center">
          You have read-only access to your child's WCC360 journey. You cannot modify assessments or reports.
        </p>
      </div>
    </>
  );
}

function ReadCard({ icon, title, status }: { icon: React.ReactNode; title: string; status: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">{icon}{title}</div>
        <div className="text-lg font-semibold">{status}</div>
      </CardContent>
    </Card>
  );
}
